import React, { useState, useRef, useEffect } from 'react';

/**
 * ChatInput component handles user input for the chat.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onSendMessage - Callback when message is sent
 * @param {string} props.placeholder - Placeholder text for input field
 * @param {string} props.buttonText - Text for send button
 * @returns {JSX.Element} Chat input component
 */
const ChatInput = ({ onSendMessage, placeholder, buttonText }) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);
  const maxRows = 5; // Maximum number of rows

  // Auto-resize the textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to properly calculate scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height based on content
    const lineHeight = 20; // line-height from CSS
    const padding = 20; // top + bottom padding
    const minHeight = lineHeight + padding; // One line + padding
    const maxHeight = lineHeight * maxRows + padding; // Max rows + padding

    // Set the height based on content, but within min/max constraints
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
  }, [message]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  return (
    <div className="talkflow-chat-input">
      <form onSubmit={handleSubmit}>
        <div className="talkflow-input-wrapper">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || 'Nhập câu hỏi của bạn...'}
            className="talkflow-input-field"
            rows="1"
          />
        </div>
        <button
          type="submit"
          className="talkflow-send-button"
          disabled={!message.trim()}
        >
          <i className="fa-solid fa-paper-plane"></i>
        </button>
      </form>
    </div>
  );
};

export default ChatInput;
